import { FirebaseFirestoreTypes } from "@react-native-firebase/firestore";
import firestore from "@react-native-firebase/firestore";

import {
  identifierToType,
  inviteErr,
  inviteOk,
  normalizeEmailLookup,
  normalizeUsernameLookup,
  type InviteResult,
  type SendGroupInvitationInput,
} from "./groupInvite.shared";

export type {
  InviteErrorCode,
  InviteIdentifier,
  InviteResult,
  SendGroupInvitationInput,
} from "./groupInvite.shared";

const lookupUser = async (
  firestoreInstance: FirebaseFirestoreTypes.Module,
  identifier: SendGroupInvitationInput["identifier"],
) => {
  const usersRef = firestoreInstance.collection("users");

  let q: FirebaseFirestoreTypes.Query;
  if (identifier.type === "email") {
    q = usersRef.where("email", "==", normalizeEmailLookup(identifier.value));
  } else if (identifier.type === "username") {
    q = usersRef.where(
      "username",
      "==",
      normalizeUsernameLookup(identifier.value),
    );
  } else {
    q = usersRef
      .where("phoneNumber.prefix", "==", identifier.prefix)
      .where("phoneNumber.number", "==", identifier.number);
  }

  const snap = await q.limit(2).get();
  if (snap.empty) return null;

  if (snap.size > 1) {
    console.warn(
      `[groupInvite] More than one user matched a ${identifier.type} identifier; picking the first.`,
      snap.docs.map((d) => d.id),
    );
  }

  const doc = snap.docs[0];
  const data = doc.data() as {
    email?: string | null;
    username?: string | null;
    phoneNumber?: { prefix?: string | null; number?: string | null } | null;
  };
  return {
    uid: doc.id,
    email: data.email ?? null,
    username: data.username ?? null,
    phoneNumber:
      data.phoneNumber &&
      data.phoneNumber.prefix &&
      data.phoneNumber.number
        ? {
            prefix: data.phoneNumber.prefix,
            number: data.phoneNumber.number,
          }
        : null,
  };
};

export const sendGroupInvitation = async (
  input: SendGroupInvitationInput,
): Promise<InviteResult> => {
  const { groupId, groupName, inviterUid, memberIds, identifier } = input;

  const target = await lookupUser(firestore(), identifier);
  if (!target) return inviteErr("USER_NOT_FOUND");
  if (target.uid === inviterUid) return inviteErr("CANNOT_INVITE_YOURSELF");
  if (memberIds.includes(target.uid)) return inviteErr("USER_ALREADY_MEMBER");

  const invitesRef = firestore().collection("groupInvitations");
  const dupSnap = await invitesRef
    .where("groupId", "==", groupId)
    .where("inviteeUid", "==", target.uid)
    .where("status", "==", "pending")
    .limit(1)
    .get();
  if (!dupSnap.empty) return inviteErr("USER_ALREADY_INVITED");

  try {
    const docRef = await invitesRef.add({
      groupId,
      groupName,
      inviterId: inviterUid,
      inviteeUid: target.uid,
      inviteeIdentifierType: identifierToType(identifier),
      inviteeEmail: target.email,
      inviteeUsername: target.username,
      inviteePhone: target.phoneNumber,
      status: "pending",
      createdAt: firestore.Timestamp.now(),
    });
    return inviteOk(docRef.id, target.uid);
  } catch (err) {
    console.error("[groupInvite] add failed", err);
    return inviteErr("INVITE_WRITE_FAILED");
  }
};
